import { UnitLabel } from '@/shared/config/config'
import { Button, InfoTooltip, Card, ProgressBar } from '@/common'
import useI18n from '@/shared/hooks/useI18n'
import { ShareArrowIcon } from '@/common/components/icons'
import { useCallback } from 'react'
import { EmissionReportStatus } from '@/api/EmissionReportModel'
import { Transition } from '@headlessui/react'
import ReportDirectEmissionsTable from './ReportDirectEmissionsTable'

export default function MiniSingleMethodEmissionReportStatusControlSection({
  reportStatus,
  activityLevel,
  reportProgress,
  submit,
  isSubmitting,
  canEditReport,
  formattedTotalDirect,
  formattedTotalIndirect,
  formattedSpecificDirect,
  formattedSpecificIndirect,
  isMiniSectionVisible,
  submittedAt,
  isActivityLevelRequired
}: {
  reportStatus: EmissionReportStatus
  activityLevel: number
  reportProgress: number
  submit: () => void
  isSubmitting: boolean
  canEditReport: boolean
  formattedTotalDirect: string
  formattedTotalIndirect: string
  formattedSpecificDirect: string
  formattedSpecificIndirect: string
  isMiniSectionVisible: boolean
  submittedAt: string | null
  isActivityLevelRequired: boolean
}) {
  const { t, dateFormatter } = useI18n()

  const handleSubmit = useCallback(() => {
    submit()
  }, [submit])

  const isSubmitted = reportStatus === EmissionReportStatus.SUBMITTED && submittedAt
  const formattedSubmittedOn = isSubmitted ? dateFormatter(new Date(submittedAt)) : ''

  return (
    <Transition
      show={isMiniSectionVisible}
      enter="transition-all duration-300 ease-in-out"
      enterFrom="opacity-100 transform translate-y-[-100%]"
      enterTo="opacity-100 transform translate-y-0"
      leave="transition-all duration-300 ease-in-out"
      leaveFrom="opacity-100 transform translate-y-0"
      leaveTo="opacity-100 transform translate-y-[-100%]"
    >
      <div className=" sticky top-0 left-0 right-0 z-50 border-b border-text-disabled">
        <Card>
          <div className="flex flex-col md:flex-row gap-4 py-1 px-4">
            {isActivityLevelRequired && (
              <div className="flex items-center md:w-1/8">
                <div className="space-y-1">
                  <div className="flex items-center gap-1">
                    <h4 className="text-sm">{t('report:activityLevel.title')}</h4>
                    <InfoTooltip
                      position="bottom"
                      content={t('report:activityLevel.fields.activityLevel.hint')}
                    />
                  </div>
                  <div className="font-medium">
                    {activityLevel} {UnitLabel.t}
                  </div>
                </div>
              </div>
            )}
            <div className="flex-1 text-sm">
              <ReportDirectEmissionsTable
                superDense
                formattedTotalDirect={formattedTotalDirect}
                formattedTotalIndirect={formattedTotalIndirect}
                formattedSpecificDirect={formattedSpecificDirect}
                formattedSpecificIndirect={formattedSpecificIndirect}
              />
            </div>
            <div className="md:w-2/6 flex items-center gap-4">
              <div className="space-y-1 w-full">
                <div className="flex items-center gap-1">
                  <h4 className="text-sm">{t('report:emissionReportStatusControl.title')}</h4>
                  <InfoTooltip
                    position="bottom"
                    content={t('report:emissionReportStatusControl.reportStatusTooltip')}
                  />
                </div>
                <div className="w-full flex align-top h-full">
                  <ProgressBar value={reportProgress} maxValue={100} />
                </div>
                {isSubmitted && (
                  <p className="flex items-center gap-2 text-sm text-text-secondary">
                    <span>
                      <ShareArrowIcon className="size-6" />
                    </span>
                    {t('report:emissionReportStatusControl.submittedOn', {
                      date: formattedSubmittedOn
                    })}
                  </p>
                )}
              </div>
              <div>
                <Button
                  sizeType="sm"
                  icon={<ShareArrowIcon />}
                  disabled={
                    reportProgress !== 100 ||
                    reportStatus === EmissionReportStatus.SUBMITTED ||
                    isSubmitting ||
                    !canEditReport
                  }
                  className="min-w-24"
                  onClick={handleSubmit}
                  isLoading={isSubmitting}
                >
                  {t('report:emissionReportStatusControl.submitReport')}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </Transition>
  )
}
